import {
	Anchor,
	PersonAvatar,
	Space
} from "client/Components";
import { CollapsibleList } from "client/Components/CollapsibleList/CollapsibleList";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useState } from "react";
import styles from "./Attendees.module.less";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { getUserProfileImageUrl } from "client/Api/UserProfile/service";
import { AttendeesDialog } from "./AttendeesDialog";
import { useHubStatisticsFormatter } from "../../useHubStatisticsFormatter";
import { NumberFormatter } from "client/Hooks";
import { AttendeeCounter, AttendeePreviewModel } from "client/Api/Hub/Events/eventViewer";
import { LocationType } from "client/Api/EventsManagement/eventsManagement";

export type AttendeesProps = {
	eventId: number;
	eventName: string;
	attendeeCounts: readonly AttendeeCounter[];
	attendees: readonly AttendeePreviewModel[];
	locationType: LocationType;
	isExportAvailable: boolean;
};

export const Attendees = (props: AttendeesProps): ReactElement => {
	const {
		eventId,
		eventName,
		attendeeCounts,
		attendees,
		locationType,
		isExportAvailable
	} = props;

	const [isAttendeesDialogOpen, setIsAttendeesDialogOpen] = useState<boolean>(false);
	const attendeesToRender: readonly AttendeePreviewModel[] = attendees.slice(0, MAX_ATTENDEES);
	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();
	const totalAttendees: number = attendeeCounts
		.reduce((sum, a) => sum + a.count, 0);

	return (
		<>
			<div className={styles.container}>
				<h4>{localize("_Attendees")}<Space />({statisticsFormatter(totalAttendees)})</h4>
				<div className={styles.list}>
					<CollapsibleList
						moreButton={() => (
							<div className={styles.buttonContainer}>
								<Anchor
									trackingId="more-button"
									className={styles.button}
									onClick={() => setIsAttendeesDialogOpen(true)}
								>
									{localize("_ShowAll")}
								</Anchor>
							</div>
						)}
						alwaysShowMoreButton
						hideOverView={false}
						trackingId="attendees-list"
					>
						{attendeesToRender.map(
							(x, index) => (
								<Item
									key={x.user
										.userId
									}
									zIndex={attendeesToRender.length - index}
									item={x}
								/>
							)
						)}
					</CollapsibleList>
				</div>
			</div>
			{isAttendeesDialogOpen && (
				<AttendeesDialog
					eventId={eventId}
					eventName={eventName}
					attendeeCounts={attendeeCounts}
					onClose={() => setIsAttendeesDialogOpen(false)}
					locationType={locationType}
					isExportAvailable={isExportAvailable}
				/>
			)}
		</>
	);
};
Attendees.displayName="Attendees";

const MAX_ATTENDEES: number = 8;

type ItemProps = {
	item: AttendeePreviewModel;
	zIndex: number;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		item,
		zIndex
	} = props;

	const {
		avatarId,
		firstName,
		lastName
	} = item.user;

	const profileImageUrl: string | null = avatarId && getUserProfileImageUrl(avatarId);
	const { initials, computedDisplayName } = getComputedDisplayName({
		firstName: firstName,
		lastName: lastName
	});

	return (
		<div
			style={{zIndex}}
			className={styles.avatar}
			title={computedDisplayName}
		>
			<PersonAvatar
				initials={initials}
				imageSrc={profileImageUrl}
			/>
		</div>
	);
};